import { useMemo, useState } from 'react'
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Plus, Search } from 'lucide-react'
import { api } from '../../../lib/api'
import { Card } from '../../ui/Card'
import { Modal } from '../../ui/Modal'
import { Button } from '../../ui/Button'
import { useConfirmare } from '../../ui/Confirmare'
import { TON } from '../../ui/TonuriB4L'
import { useAuthStore } from '../../../stores/auth'
import { ECHIPA_MARJA_ZILE } from '@b4l/shared'

/**
 * „Echipa la eveniment" (24.10.2026, cerut de Dorian): cine din firma merge si in ce zile, ca Victor
 * sa vada cine e unde. Optionala — gol, blocul arata doar „+ Alocă colegi".
 *
 * Conflictele vin gata calculate de server (concediu / indisponibil, alt eveniment in aceeasi zi),
 * la fiecare citire: un concediu inregistrat dupa alocare apare si el. Avertizeaza, nu blocheaza.
 * Culorile urmeaza research-ul din plan: ⛱ galben pentru absenta, ⚠ rosu doar pentru doua locuri.
 */

export interface ConflictZi {
  zi: string
  fel: 'concediu' | 'indisponibil' | 'eveniment'
  campanieId?: number
  titlu?: string
}

export interface OmInEchipa {
  userId: number
  nume: string
  functie: string | null
  rolUser: string
  activ: boolean
  rol: string | null
  program: string | null
  zile: string[]
  anuntat: boolean
  conflicte: ConflictZi[]
}

export interface RaspunsEchipa {
  provizorie: boolean
  poateAloca: boolean
  echipa: OmInEchipa[]
}

interface Coleg {
  id: number
  name: string
  jobTitle: string | null
}

interface CalendarRaspuns {
  absenta: 'concediu' | 'indisponibil'
  cuAbsente: boolean
  oameni: {
    userId: number
    alocari: { zi: string; campanieId: number; titlu: string }[]
    absente: string[]
  }[]
}

const ZI = 86_400_000
const zileIntre = (de: string, pana: string): string[] => {
  const out: string[] = []
  for (let t = Date.parse(`${de}T00:00:00Z`); t <= Date.parse(`${pana}T00:00:00Z`); t += ZI)
    out.push(new Date(t).toISOString().slice(0, 10))
  return out
}
const ZI_SCURT = new Intl.DateTimeFormat('ro-RO', {
  weekday: 'short',
  day: 'numeric',
  month: 'short',
  timeZone: 'UTC',
})
export const ziScurt = (z: string) => ZI_SCURT.format(new Date(`${z}T00:00:00Z`))
const initiale = (n: string) =>
  n
    .split(/\s+/)
    .slice(0, 2)
    .map((p) => p[0] ?? '')
    .join('')
    .toUpperCase()

/** Mesajul de eroare al API-ului, ca fereastra sa spuna ce nu merge. */
const mesaj = (e: unknown, implicit: string) =>
  (e as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error
    ?.message ?? implicit

export function cheieEchipa(id: number) {
  return ['campanie', id, 'echipa'] as const
}

export function EchipaEveniment({
  campanieId,
  dataStart,
  dataFinal,
  titlu,
}: {
  campanieId: number
  dataStart: string
  dataFinal: string | null
  titlu: string
}) {
  const [aloca, setAloca] = useState(false)
  const [editat, setEditat] = useState<OmInEchipa | null>(null)
  const q = useQuery<RaspunsEchipa>({
    queryKey: cheieEchipa(campanieId),
    queryFn: async () => (await api.get<RaspunsEchipa>(`/campanii/${campanieId}/echipa`)).data,
    // 403: omul vede activitatea prin Sampling, nu prin PR & Evenimente — blocul nu apare deloc.
    retry: (n, e) => (e as { response?: { status?: number } })?.response?.status !== 403 && n < 2,
  })
  const status = (q.error as { response?: { status?: number } } | null)?.response?.status
  if (status === 403) return null

  const d = q.data
  const perioada = zileIntre(dataStart.slice(0, 10), (dataFinal ?? dataStart).slice(0, 10))
  const deLamurit = d?.echipa.filter((o) => o.conflicte.length > 0).length ?? 0

  return (
    <Card
      title="Echipa la eveniment"
      actions={
        d?.poateAloca ? (
          <Button variant="secondary" onClick={() => setAloca(true)} className="!py-1 !text-mic">
            <Plus size={14} /> Alocă colegi
          </Button>
        ) : undefined
      }
    >
      {q.isLoading ? (
        <p className="text-mic text-b4l-text-muted">Se încarcă…</p>
      ) : q.isError ? (
        <p className="text-mic text-b4l-red-dark">
          Echipa n-a putut fi încărcată.{' '}
          <button type="button" onClick={() => q.refetch()} className="underline">
            Reîncearcă
          </button>
        </p>
      ) : !d || d.echipa.length === 0 ? (
        <p className="text-mic text-b4l-text-muted">
          Nu merge nimeni de la noi.
          {d?.poateAloca && ' Dacă merge cineva, apasă „Alocă colegi”.'}
        </p>
      ) : (
        <div className="grid gap-2">
          {d.provizorie && (
            <p className={`rounded-lg px-3 py-2 text-mic ${TON.gri.pastila}`}>
              <b className="font-semibold">Alocare provizorie.</b> Activitatea e încă propunere:
              colegii vor fi anunțați când se confirmă.
            </p>
          )}
          {deLamurit > 0 && (
            <p className="text-mic text-b4l-text-muted">
              {deLamurit === 1 ? 'Un coleg are' : `${deLamurit} colegi au`} ceva de lămurit în
              zilele alocate.
            </p>
          )}
          <ul className="divide-y divide-b4l-border">
            {d.echipa.map((o) => (
              <RandOm
                key={o.userId}
                o={o}
                provizorie={d.provizorie}
                poateAloca={d.poateAloca}
                onSchimba={() => setEditat(o)}
              />
            ))}
          </ul>
        </div>
      )}

      {aloca && d && (
        <FereastraAlocare
          campanieId={campanieId}
          titlu={titlu}
          perioada={perioada}
          dejaAlocati={d.echipa.map((o) => o.userId)}
          provizorie={d.provizorie}
          onClose={() => setAloca(false)}
        />
      )}
      {editat && (
        <FereastraOm
          campanieId={campanieId}
          perioada={perioada}
          om={editat}
          provizorie={d?.provizorie ?? false}
          onClose={() => setEditat(null)}
        />
      )}
    </Card>
  )
}

function RandOm({
  o,
  provizorie,
  poateAloca,
  onSchimba,
}: {
  o: OmInEchipa
  provizorie: boolean
  poateAloca: boolean
  onSchimba: () => void
}) {
  const absente = new Set(o.conflicte.filter((c) => c.fel !== 'eveniment').map((c) => c.zi))
  const dubluri = new Map<string, string[]>()
  for (const c of o.conflicte.filter((x) => x.fel === 'eveniment'))
    dubluri.set(c.zi, [...(dubluri.get(c.zi) ?? []), c.titlu ?? 'altă activitate'])
  const absenta = o.conflicte.find((c) => c.fel !== 'eveniment')?.fel
  return (
    <li className="grid grid-cols-[28px_minmax(0,1fr)_auto] items-start gap-2.5 py-2.5 first:pt-0">
      <span
        aria-hidden
        className={`grid h-7 w-7 place-items-center rounded-full text-[11px] font-bold ${TON.albastru.pastila}`}
      >
        {initiale(o.nume)}
      </span>
      <div className="min-w-0">
        <div className="flex flex-wrap items-baseline gap-x-2">
          <b className="text-corp font-semibold text-b4l-text">{o.nume}</b>
          {o.functie && <span className="text-mic text-b4l-text-dim">{o.functie}</span>}
          {!o.activ && <span className="text-mic text-b4l-red-dark">cont dezactivat</span>}
        </div>
        {(o.rol || o.program) && (
          <p className="text-mic text-b4l-text-muted">
            {[o.rol, o.program].filter(Boolean).join(' · ')}
          </p>
        )}
        <div className="mt-1 flex flex-wrap gap-1">
          {o.zile.map((z) => {
            const dublu = dubluri.has(z)
            const absent = absente.has(z)
            return (
              <span
                key={z}
                className={`rounded-md px-1.5 py-px text-[12px] ${
                  dublu
                    ? TON.rosu.pastila
                    : absent
                      ? `${TON.galben.pastila} bg-[repeating-linear-gradient(135deg,transparent_0_5px,rgb(var(--b4l-surface)/0.6)_5px_10px)]`
                      : provizorie
                        ? 'border border-dashed border-b4l-border text-b4l-text-muted'
                        : 'border border-b4l-border bg-b4l-bg-soft text-b4l-text'
                }`}
              >
                {dublu ? '⚠ ' : absent ? '⛱ ' : ''}
                {ziScurt(z)}
              </span>
            )
          })}
        </div>
        {[...dubluri].map(([z, unde]) => (
          <p
            key={z}
            role="note"
            className={`mt-1.5 rounded-md px-2 py-1 text-mic ${TON.rosu.pastila}`}
          >
            ⚠ Și la: <b className="font-semibold">{unde.join(', ')}</b>, {ziScurt(z)}. Lămuriți unde
            merge.
          </p>
        ))}
        {absente.size > 0 && (
          <p role="note" className={`mt-1.5 rounded-md px-2 py-1 text-mic ${TON.galben.pastila}`}>
            ⛱ {absenta === 'concediu' ? 'În concediu' : 'Indisponibil'}:{' '}
            {[...absente].map(ziScurt).join(', ')}.
          </p>
        )}
      </div>
      {poateAloca && (
        <button
          type="button"
          onClick={onSchimba}
          className="text-mic text-b4l-blue-dark hover:underline"
          aria-label={`Schimbă alocarea lui ${o.nume}`}
        >
          Schimbă
        </button>
      )}
    </li>
  )
}

/** Limita campului „altă zi": cel mult `ECHIPA_MARJA_ZILE` de perioada activitatii, ca pe server. */
const marja = (z: string | undefined, semn: 1 | -1) =>
  z
    ? new Date(Date.parse(`${z}T00:00:00Z`) + semn * ECHIPA_MARJA_ZILE * ZI)
        .toISOString()
        .slice(0, 10)
    : undefined

/** Zilele: cele ale activitatii, gata pe ecran, plus „altă zi" (montajul din ajun, demontajul). */
function AlegeZile({
  perioada,
  alese,
  setAlese,
}: {
  perioada: string[]
  alese: string[]
  setAlese: (z: string[]) => void
}) {
  const [alta, setAlta] = useState('')
  const toate = [...new Set([...perioada, ...alese])].sort()
  const comuta = (z: string) =>
    setAlese(alese.includes(z) ? alese.filter((x) => x !== z) : [...alese, z].sort())
  return (
    <fieldset className="grid gap-1.5">
      <legend className="text-mic text-b4l-text-muted">Zile</legend>
      <div className="flex flex-wrap items-center gap-1.5">
        {toate.map((z) => {
          const afara = !perioada.includes(z)
          const on = alese.includes(z)
          return (
            <button
              key={z}
              type="button"
              aria-pressed={on}
              onClick={() => comuta(z)}
              className={`grid rounded-md border px-2 py-1 text-left leading-tight ${
                afara ? 'border-dashed' : ''
              } ${
                on
                  ? 'border-b4l-blue bg-b4l-blue-light text-b4l-blue-dark'
                  : 'border-b4l-border bg-b4l-surface text-b4l-text'
              }`}
            >
              <span className="text-[10.5px] text-b4l-text-dim">
                {afara ? 'în afara activității' : `ziua ${perioada.indexOf(z) + 1}`}
              </span>
              <span className="text-mic font-semibold">{ziScurt(z)}</span>
            </button>
          )
        })}
        <label className="inline-flex items-center gap-1 text-mic text-b4l-text-muted">
          <span className="sr-only">Altă zi</span>
          <input
            id="echipa-alta-zi"
            type="date"
            min={marja(perioada[0], -1)}
            max={marja(perioada[perioada.length - 1], 1)}
            value={alta}
            onChange={(e) => setAlta(e.target.value)}
            className="rounded-md border border-b4l-border bg-b4l-surface px-2 py-1 text-mic text-b4l-text"
          />
          <button
            type="button"
            disabled={!alta}
            onClick={() => {
              if (alta && !alese.includes(alta)) setAlese([...alese, alta].sort())
              setAlta('')
            }}
            className="rounded-md px-2 py-1 text-b4l-blue-dark hover:underline disabled:opacity-40"
          >
            + altă zi
          </button>
        </label>
      </div>
    </fieldset>
  )
}

function CampuriDetalii({
  rol,
  setRol,
  program,
  setProgram,
}: {
  rol: string
  setRol: (s: string) => void
  program: string
  setProgram: (s: string) => void
}) {
  return (
    <div className="flex flex-wrap gap-3">
      <label className="grid min-w-[200px] flex-[2] gap-1 text-mic text-b4l-text-muted">
        Ce face acolo
        <input
          id="echipa-rol"
          value={rol}
          maxLength={120}
          onChange={(e) => setRol(e.target.value)}
          placeholder="ex: stand și prezentări"
          className="rounded-md border border-b4l-border bg-b4l-surface px-2.5 py-1.5 text-corp text-b4l-text"
        />
      </label>
      <label className="grid min-w-[140px] flex-1 gap-1 text-mic text-b4l-text-muted">
        Program (opțional)
        <input
          id="echipa-program"
          value={program}
          maxLength={60}
          onChange={(e) => setProgram(e.target.value)}
          placeholder="ex: 10:00–18:00"
          className="rounded-md border border-b4l-border bg-b4l-surface px-2.5 py-1.5 text-corp text-b4l-text"
        />
      </label>
    </div>
  )
}

/** Tabelul liber / indisponibil / alt eveniment, pentru oamenii si zilele alese. */
function Disponibilitate({
  userIds,
  zile,
  nume,
}: {
  userIds: number[]
  zile: string[]
  nume: Map<number, string>
}) {
  const de = zile[0]
  const pana = zile[zile.length - 1]
  const q = useQuery<CalendarRaspuns>({
    queryKey: ['echipa-calendar', de, pana, userIds.join(',')],
    queryFn: async () =>
      (
        await api.get<CalendarRaspuns>('/campanii/echipa/calendar', {
          params: { deLa: de, panaLa: pana, userIds: userIds.join(',') },
        })
      ).data,
    enabled: userIds.length > 0 && zile.length > 0,
    // Tabelul ramane pe ecran cat se verifica noua alegere — nu clipeste la fiecare zi bifata.
    placeholderData: keepPreviousData,
  })
  if (userIds.length === 0 || zile.length === 0) return null
  if (q.isError)
    return (
      <p className="text-mic text-b4l-red-dark">
        Disponibilitatea n-a putut fi verificată. Poți aloca oricum; conflictele apar apoi pe
        activitate.
      </p>
    )
  if (!q.data) return <p className="text-mic text-b4l-text-muted">Se verifică disponibilitatea…</p>
  const absenta = q.data.absenta === 'concediu' ? 'concediu' : 'indisponibil'
  return (
    <div
      className="overflow-x-auto rounded-lg border border-b4l-border"
      role="region"
      aria-label="Disponibilitatea colegilor aleși"
    >
      <table className="w-full min-w-[420px] border-collapse text-[12px]">
        <thead>
          <tr className="bg-b4l-bg-soft text-b4l-text-muted">
            <th className="px-2 py-1.5 text-left font-semibold">Disponibilitate</th>
            {zile.map((z) => (
              <th key={z} className="px-2 py-1.5 font-semibold">
                {ziScurt(z)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {userIds.map((id) => {
            const o = q.data!.oameni.find((x) => x.userId === id)
            return (
              <tr key={id} className="border-t border-b4l-border">
                <td className="px-2 py-1.5 text-b4l-text">{nume.get(id) ?? `#${id}`}</td>
                {zile.map((z) => {
                  const unde = o?.alocari.filter((a) => a.zi === z) ?? []
                  const absent = o?.absente.includes(z)
                  return (
                    <td
                      key={z}
                      className={`px-2 py-1.5 text-center ${
                        unde.length
                          ? `${TON.rosu.pastila} font-semibold`
                          : absent
                            ? `${TON.galben.pastila} font-semibold`
                            : 'text-b4l-green-dark'
                      }`}
                    >
                      {unde.length
                        ? `⚠ ${unde.map((u) => u.titlu).join(', ')}`
                        : absent
                          ? `⛱ ${absenta}`
                          : 'liber'}
                    </td>
                  )
                })}
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

function FereastraAlocare({
  campanieId,
  titlu,
  perioada,
  dejaAlocati,
  provizorie,
  onClose,
}: {
  campanieId: number
  titlu: string
  perioada: string[]
  dejaAlocati: number[]
  provizorie: boolean
  onClose: () => void
}) {
  const qc = useQueryClient()
  const [cauta, setCauta] = useState('')
  const [alesi, setAlesi] = useState<number[]>([])
  const [zile, setZile] = useState<string[]>(perioada.length <= 7 ? perioada : [])
  const [rol, setRol] = useState('')
  const [program, setProgram] = useState('')
  // Aceeasi cheie si aceeasi forma ca pe pagina activitatii: cache-ul e comun.
  const colegi = useQuery<{ users: Coleg[] }>({
    queryKey: ['settings-users-scurt'],
    queryFn: async () => (await api.get<{ users: Coleg[] }>('/settings/users/echipa')).data,
  })
  const lista = useMemo(() => colegi.data?.users ?? [], [colegi.data])
  const nume = useMemo(() => new Map(lista.map((c) => [c.id, c.name])), [lista])
  const vizibili = lista.filter(
    (c) =>
      !dejaAlocati.includes(c.id) &&
      (!cauta.trim() || c.name.toLowerCase().includes(cauta.trim().toLowerCase())),
  )
  const salveaza = useMutation({
    mutationFn: async () =>
      (
        await api.post<RaspunsEchipa>(`/campanii/${campanieId}/echipa`, {
          userIds: alesi,
          zile,
          rol: rol.trim() || null,
          program: program.trim() || null,
        })
      ).data,
    onSuccess: (d) => {
      qc.setQueryData(cheieEchipa(campanieId), d)
      qc.invalidateQueries({ queryKey: ['echipa-calendar'] })
      // Rezumatul din dreapta si lista citesc echipa pe scurt din activitate.
      qc.invalidateQueries({ queryKey: ['campanie', campanieId], exact: true })
      qc.invalidateQueries({ queryKey: ['campanii'] })
      toast.success(alesi.length === 1 ? 'Colegul a fost alocat' : `${alesi.length} colegi alocați`)
      onClose()
    },
    onError: (e) => toast.error(mesaj(e, 'Alocarea n-a putut fi salvată')),
  })
  const gata = alesi.length > 0 && zile.length > 0
  return (
    <Modal
      open
      onClose={onClose}
      title={`Alocă colegi la ${titlu}`}
      size="lg"
      footer={
        <div className="flex w-full flex-wrap items-center justify-between gap-2">
          <span className="text-mic text-b4l-text-muted">
            {alesi.length === 0
              ? 'Alege cel puțin un coleg.'
              : zile.length === 0
                ? 'Alege cel puțin o zi.'
                : provizorie
                  ? 'Activitatea e încă propunere: vor fi anunțați când se confirmă.'
                  : 'Primesc o notificare în CRM, cu zilele și ce au de făcut.'}
          </span>
          <div className="flex gap-2">
            <Button variant="secondary" onClick={onClose}>
              Renunță
            </Button>
            <Button disabled={!gata} loading={salveaza.isPending} onClick={() => salveaza.mutate()}>
              {alesi.length > 1 ? `Alocă ${alesi.length} colegi` : 'Alocă'}
            </Button>
          </div>
        </div>
      }
    >
      <div className="grid gap-4">
        <div className="grid gap-1.5">
          <label htmlFor="echipa-cauta" className="text-mic text-b4l-text-muted">
            Colegi
          </label>
          <div className="flex items-center gap-2 rounded-md border border-b4l-border bg-b4l-surface px-2.5">
            <Search size={14} className="text-b4l-text-dim" aria-hidden />
            <input
              id="echipa-cauta"
              value={cauta}
              onChange={(e) => setCauta(e.target.value)}
              placeholder="Caută după nume"
              className="w-full bg-transparent py-1.5 text-corp text-b4l-text outline-none"
            />
          </div>
          <ul className="max-h-48 overflow-y-auto rounded-md border border-b4l-border">
            {colegi.isLoading && (
              <li className="px-2.5 py-2 text-mic text-b4l-text-muted">Se încarcă…</li>
            )}
            {vizibili.map((c) => (
              <li key={c.id}>
                <label className="flex cursor-pointer items-center gap-2 px-2.5 py-1.5 text-corp hover:bg-b4l-bg-soft">
                  <input
                    type="checkbox"
                    checked={alesi.includes(c.id)}
                    onChange={() =>
                      setAlesi(
                        alesi.includes(c.id) ? alesi.filter((x) => x !== c.id) : [...alesi, c.id],
                      )
                    }
                  />
                  <span className="text-b4l-text">{c.name}</span>
                  {c.jobTitle && <span className="text-mic text-b4l-text-dim">{c.jobTitle}</span>}
                </label>
              </li>
            ))}
            {!colegi.isLoading && vizibili.length === 0 && (
              <li className="px-2.5 py-2 text-mic text-b4l-text-muted">
                Niciun coleg pentru „{cauta}”.
              </li>
            )}
          </ul>
        </div>
        <AlegeZile perioada={perioada} alese={zile} setAlese={setZile} />
        <CampuriDetalii rol={rol} setRol={setRol} program={program} setProgram={setProgram} />
        <Disponibilitate userIds={alesi} zile={zile} nume={nume} />
        {alesi.length > 0 && zile.length > 0 && (
          <p className="text-mic text-b4l-text-muted">
            Se poate salva și cu suprapuneri: rămân semnalate pe activitate și în calendarul
            echipei, până le lămurește cineva.
          </p>
        )}
      </div>
    </Modal>
  )
}

function FereastraOm({
  campanieId,
  perioada,
  om,
  provizorie,
  onClose,
}: {
  campanieId: number
  perioada: string[]
  om: OmInEchipa
  provizorie: boolean
  onClose: () => void
}) {
  const euId = useAuthStore((s) => s.user?.id)
  const eu = euId === om.userId
  const qc = useQueryClient()
  const { confirma } = useConfirmare()
  const [zile, setZile] = useState(om.zile)
  const [rol, setRol] = useState(om.rol ?? '')
  const [program, setProgram] = useState(om.program ?? '')
  const gata = (d: RaspunsEchipa, text: string) => {
    qc.setQueryData(cheieEchipa(campanieId), d)
    qc.invalidateQueries({ queryKey: ['echipa-calendar'] })
    // Rezumatul din dreapta si lista citesc echipa pe scurt din activitate.
    qc.invalidateQueries({ queryKey: ['campanie', campanieId], exact: true })
    qc.invalidateQueries({ queryKey: ['campanii'] })
    toast.success(text)
    onClose()
  }
  const salveaza = useMutation({
    mutationFn: async () =>
      (
        await api.put<RaspunsEchipa>(`/campanii/${campanieId}/echipa/${om.userId}`, {
          zile,
          rol: rol.trim() || null,
          program: program.trim() || null,
        })
      ).data,
    onSuccess: (d) => gata(d, 'Alocarea a fost salvată'),
    onError: (e) => toast.error(mesaj(e, 'Alocarea n-a putut fi salvată')),
  })
  const scoate = useMutation({
    mutationFn: async () =>
      (await api.delete<RaspunsEchipa>(`/campanii/${campanieId}/echipa/${om.userId}`)).data,
    onSuccess: (d) => gata(d, `${om.nume} nu mai e în echipă`),
    onError: (e) => toast.error(mesaj(e, 'Colegul n-a putut fi scos')),
  })
  return (
    <Modal
      open
      onClose={onClose}
      title={om.nume}
      size="lg"
      footer={
        <div className="flex w-full flex-wrap items-center justify-between gap-2">
          <Button
            variant="secondary"
            loading={scoate.isPending}
            onClick={async () => {
              if (
                await confirma({
                  titlu: `Scoți pe ${om.nume} din echipă?`,
                  mesaj: eu
                    ? 'Te scoți din echipa acestei activități.'
                    : om.anuntat
                      ? 'Primește o notificare că nu mai merge.'
                      : 'Nu fusese anunțat încă, deci nu află nimic.',
                  buton: 'Scoate din echipă',
                  pericol: true,
                })
              )
                scoate.mutate()
            }}
            className="!text-b4l-red-dark"
          >
            Scoate din echipă
          </Button>
          <div className="flex gap-2">
            <Button variant="secondary" onClick={onClose}>
              Renunță
            </Button>
            <Button
              disabled={zile.length === 0}
              loading={salveaza.isPending}
              onClick={() => salveaza.mutate()}
            >
              Salvează
            </Button>
          </div>
        </div>
      }
    >
      <div className="grid gap-4">
        <AlegeZile perioada={perioada} alese={zile} setAlese={setZile} />
        <CampuriDetalii rol={rol} setRol={setRol} program={program} setProgram={setProgram} />
        <p className="text-mic text-b4l-text-muted">
          {eu
            ? 'Îți schimbi propriile zile: nu primești notificare.'
            : provizorie
              ? 'Activitatea e încă propunere: află noile zile când se confirmă.'
              : om.anuntat
                ? 'Dacă schimbi zilele, primește o notificare. Ce face și programul se schimbă fără anunț.'
                : 'Primește o notificare dacă îi schimbi zilele.'}
        </p>
      </div>
    </Modal>
  )
}
